import { z } from 'zod';
import { apiGet, apiPost, apiSend } from '@/lib/api';

/**
 * ⚠️ NAZEWNICTWO: `spsUserNumber` to NUMER KARTY (`Users.USERNUMBER`) i zarazem klucz główny
 * w SPS — nie „numer pracownika". `alias` (`Users.ALIAS`) to numer DODATKOWY, nie karta.
 * Wcześniej te dwa pola nazywały się `spsUserId` i `cardNo`, co mówiło dokładnie odwrotnie
 * o tym, co jest czym.
 *
 * Numer karty jest TEKSTEM: u klientów realnie `'0009999'`, z wiodącymi zerami, których
 * liczba by nie zachowała.
 */
export const EmployeeRowSchema = z.object({
  id: z.string(),
  spsUserNumber: z.string(),
  name: z.string(),
  alias: z.string().nullable(),
  costCenter: z.string().nullable(),
  active: z.boolean(),
  /**
   * `Users.RATIONEFFECT` — 3 = egzekwuj limity, 2 = ignoruj ale loguj, 1 = ignoruj całkowicie.
   * Przy 1 albo 2 automat NIE pilnuje żadnego limitu tej osoby.
   */
  rationEffect: z.number(),
  groups: z.array(z.object({ id: z.string(), name: z.string() })),
  machineCount: z.number(),
  /** Automaty tylko przez grupę, poza przypisanymi wprost (14.10.2026); starsze API — zero. */
  machineCountZGrup: z.number().default(0),
  /** true = pobiera na >1 automacie → limity per-automat liczyłyby go błędnie */
  multiMachine: z.boolean(),
  pending: z.boolean(),
  /** Ile reguł limitu obejmuje osobę — wprost albo przez którąkolwiek z jej grup. */
  limits: z.number(),
  /** Wydania w oknie aktywności (`oknoDni`). */
  txCount: z.number(),
  /** Ostatnie wydanie kiedykolwiek — czas ŚCIENNY, bez strefy. */
  lastTxAt: z.string().nullable(),
  /**
   * USUNIĘTA Z AUTOMATÓW (29.09.2026) — kiedy (ISO); brak albo `null` = jest na automatach. `nullish`,
   * bo front bywa nowszy od API i starsza instancja tego pola nie wysyła.
   */
  usunietaZAutomatow: z.string().nullish(),
  /** Węzeł, na którym osobę usunięto w SPSUI; brak = z portalu. */
  usunietaZAutomatowWezel: z.string().nullish(),
  /**
   * ZNACZNIK HR+ (12.10.2026): `HR_PLUS` | `HR_PLUS_ZABLOKOWANA` | `SPOZA_HR`; `null` — wyspa bez HR+.
   * Napis, nie `enum`: nowy stan z serwera nie może zamienić listy w błąd.
   */
  hrPlus: z.string().nullish(),
  hrPlusNumer: z.string().nullish(),
});

export type EmployeeRow = z.infer<typeof EmployeeRowSchema>;

const EmployeesPageSchema = z.object({
  rows: z.array(EmployeeRowSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  /** Udział + JAWNY mianownik — etykieta w UI musi go nazwać, nie domyślać. */
  multiMachine: z.object({
    share: z.number().nullable(),
    withAccess: z.number(),
    multi: z.number(),
    activeTotal: z.number(),
  }),
  /** Kafelki — zawsze o CAŁEJ wyspie, niezależnie od filtrów. */
  summary: z.object({
    wszyscy: z.number(),
    aktywni: z.number(),
    zablokowani: z.number(),
    wGrupach: z.number(),
    zLimitami: z.number(),
    aktywniOstatnio: z.number(),
    /** Usunięci z automatów — poza pozostałymi kafelkami. */
    usunieci: z.number().optional(),
  }),
  wgGrupy: z.array(z.object({ id: z.string(), name: z.string(), count: z.number() })),
  /** Ile dni liczy okno aktywności — etykieta bierze liczbę stąd, nie wpisuje jej. */
  oknoDni: z.number(),
  /** Wyspa ma ustawienia HR+ — wtedy znaczniki i filtr HR+. */
  hrPlusUstawiony: z.boolean().default(false),
});

export const EmployeeGroupSchema = z.object({
  id: z.string(),
  /** Kod grupy — TEKST, u klientów zwykle słowo ('BIURO', 'PRODU'). */
  spsGroupNumber: z.string(),
  name: z.string(),
  _count: z.object({ members: z.number() }),
});

export type EmployeeGroup = z.infer<typeof EmployeeGroupSchema>;

export type ListEmployeesParams = {
  groupId: string;
  search?: string;
  employeeGroupId?: string;
  machineId?: string;
  costCenter?: string;
  /**
   * Trójstan. Sam przełącznik nie umiał powiedzieć „pokaż WYŁĄCZNIE nieaktywnych", a to jedyne
   * pytanie, które się przy tym filtrze realnie zadaje.
   */
  stan: 'aktywni' | 'nieaktywni' | 'wszyscy';
  /** Usunięci z automatów — domyślnie ukryci; `tylko` pokazuje wyłącznie ich (29.09.2026). */
  usunieci?: 'ukryj' | 'tylko';
  /** Znacznik HR+: `z` — powiązani z HR+, `spoza` — niepowiązani (12.10.2026). */
  hrPlus?: 'z' | 'spoza' | 'wyjatki';
  onlyMultiMachine: boolean;
  page: number;
  pageSize: number;
  sort: string;
  dir: 'asc' | 'desc';
};

export const listEmployees = (p: ListEmployeesParams) => {
  const q = new URLSearchParams({
    groupId: p.groupId,
    stan: p.stan,
    onlyMultiMachine: String(p.onlyMultiMachine),
    page: String(p.page),
    pageSize: String(p.pageSize),
    sort: p.sort,
    dir: p.dir,
  });
  if (p.search) q.set('search', p.search);
  if (p.employeeGroupId) q.set('employeeGroupId', p.employeeGroupId);
  if (p.machineId) q.set('machineId', p.machineId);
  if (p.costCenter) q.set('costCenter', p.costCenter);
  if (p.usunieci) q.set('usunieci', p.usunieci);
  if (p.hrPlus) q.set('hrPlus', p.hrPlus);
  return apiGet(`/employees?${q.toString()}`, EmployeesPageSchema);
};

export const fetchEmployeeGroups = (groupId: string) =>
  apiGet(`/employees/groups?groupId=${groupId}`, z.array(EmployeeGroupSchema));

/**
 * Czy numer karty jest wolny.
 *
 * ZASTĄPIŁO `fetchNextUserId`. Numeru karty nie ma czego podpowiadać — jest wybity na karcie;
 * portal może tylko sprawdzić kolizję. Sprawdzenie jest niewrażliwe na wielkość liter (kolacja
 * SPS) i patrzy też na kolejkę: karta może być wolna w read-modelu i jednocześnie zajęta
 * operacją, która czeka na wykonanie.
 */
export const checkCardNumber = (groupId: string, cardNumber: string) =>
  apiGet(
    `/employees/check-card?groupId=${groupId}&cardNumber=${encodeURIComponent(cardNumber)}`,
    z.object({
      cardNumber: z.string(),
      free: z.boolean(),
      takenBy: z
        .object({ id: z.string(), name: z.string(), spsUserNumber: z.string() })
        .nullable(),
      /** Karta usunięta z automatów — dodanie osoby z tym numerem ją przywróci. */
      usunietaZAutomatow: z.boolean().optional(),
      pending: z.boolean(),
    }),
  );

/**
 * `check-card` CZYTANE PRZY ZMIANIE NUMERU — inaczej niż przy zakładaniu osoby.
 *
 * ⚠️ „Wolna” z serwera odpowiada na pytanie o ZAKŁADANIE: numer osoby usuniętej z automatów jest wtedy
 * do PRZYWRÓCENIA (ten sam plastik dla kolejnego człowieka, decyzja z 29.09.2026). Przy ZMIANIE numeru
 * serwer taki numer odrzuca (`409`), bo trzyma go w Vendi tamta osoba razem ze swoimi wydaniami — okno
 * puszczało więc zapis, który i tak padał. Ta sama reguła stoi w opisie trasy dla Vending+.
 */
export function kartaDoZmianyNumeru<T extends { free: boolean; usunietaZAutomatow?: boolean }>(
  r: T,
): T & { usunieta: boolean } {
  const usunieta = r.usunietaZAutomatow === true;
  return { ...r, free: r.free && !usunieta, usunieta };
}

/**
 * ZNACZNIKI `Users` — pary (pole, kolumna SPS) w JEDNYM miejscu po stronie ekranu.
 *
 * ⚠️ NAZWA KOLUMNY JEST CZĘŚCIĄ DANYCH, nie komentarzem. Nie mamy dokumentacji SupplyPointa —
 * `SPS-SCHEMA.md` daje nazwy, typy i rozmiary, i tyle. Polskie etykiety są NASZYM odczytaniem
 * tych nazw, więc formularz pokazuje kolumnę obok etykiety: to ona pozwala zestawić pole 1:1
 * z tym, co operator widzi w SPSUtilities przy automacie.
 */
export const ZNACZNIKI_PRACOWNIKA = [
  /**
   * ⚠️ `domyslnie` DOTYCZY WYŁĄCZNIE NOWEGO PRACOWNIKA. Przy edycji wartość zawsze pochodzi
   * z SPS — podstawienie domyślnej nadpisałoby ustawienie zrobione przy automacie, i to bez
   * objawu aż do chwili, gdy ktoś stanie przed maszyną.
   *
   * Te dwa są włączone, bo tak wygląda kadra u klienta: na DBW `RETURNTOMENU` i
   * `SINGLECOMPRESTRICT` ma większość pracowników. Zakładanie nowej osoby z odwrotnym
   * ustawieniem znaczyło, że ktoś musi o tym pamiętać przy każdym dodaniu.
   */
  { pole: 'returnToMenu', kolumna: 'RETURNTOMENU', domyslnie: true },
  { pole: 'singleCompRestrict', kolumna: 'SINGLECOMPRESTRICT', domyslnie: true },
  { pole: 'allowDisabledItems', kolumna: 'ALLOWDISABLEDITEMS' },
  { pole: 'dispenseUsedFirst', kolumna: 'DISPENSEUSEDFIRST' },
  { pole: 'offerConUncon', kolumna: 'OFFERCONUNCON' },
  { pole: 'canAccessExternalStock', kolumna: 'CANACCESSEXTERNALSTOCK' },
  { pole: 'canSizeGrid', kolumna: 'CANSIZEGRID' },
  { pole: 'kitChangeIssue', kolumna: 'KITCHANGEISSUE' },
  { pole: 'kitExceed', kolumna: 'KITEXCEED' },
  { pole: 'kitIgnoreShortage', kolumna: 'KITIGNORESHORTAGE' },
  { pole: 'ignoreSwap', kolumna: 'IGNORESWAP' },
  { pole: 'ignoreStrictReturnRule', kolumna: 'IGNORESTRICTRETURNRULE' },
  { pole: 'forceCountersign', kolumna: 'FORCECOUNTERSIGN' },
  { pole: 'skipFpCheck', kolumna: 'SKIPFPCHECK' },
  { pole: 'isHhAdmin', kolumna: 'ISHHADMIN' },
  { pole: 'demoModeOnly', kolumna: 'DEMOMODEONLY' },
] as const;

export type PoleZnacznika = (typeof ZNACZNIKI_PRACOWNIKA)[number]['pole'];

const ZnacznikiSchema = z.object(
  Object.fromEntries(ZNACZNIKI_PRACOWNIKA.map(({ pole }) => [pole, z.boolean()])) as Record<
    PoleZnacznika,
    z.ZodBoolean
  >,
);

/**
 * Pełny stan pracownika — pod formularz edycji.
 *
 * ⚠️ NIE MA TU PIN-U i nie będzie: agent kolumny `PASSWORD` nie czyta. Pole PIN-u na formularzu
 * jest puste ZAWSZE i to jest cecha, nie brakujące dane.
 */
export const EmployeeDetailSchema = ZnacznikiSchema.extend({
  id: z.string(),
  groupId: z.string(),
  /**
   * USUNIĘTA Z AUTOMATÓW (29.09.2026) — kiedy (ISO); brak albo `null` = jest na automatach. `nullish`,
   * bo front bywa nowszy od API i starsza instancja tego pola nie wysyła.
   */
  usunietaZAutomatow: z.string().nullish(),
  /** Węzeł, na którym osobę usunięto w SPSUI; brak = z portalu. */
  usunietaZAutomatowWezel: z.string().nullish(),

  spsUserNumber: z.string(),
  name: z.string(),
  alias: z.string().nullable(),
  costCenter: z.string().nullable(),
  active: z.boolean(),
  rationEffect: z.number(),
  dateRangeEnable: z.boolean(),
  /** Data ŚCIENNA `RRRR-MM-DD` — bez strefy, bo to data kalendarzowa maszyny klienta. */
  dateRangeStart: z.string().nullable(),
  dateRangeEnd: z.string().nullable(),
  employeeGroupIds: z.array(z.string()),
  /** Automaty przypisane BEZPOŚREDNIO — tylko te da się odpiąć z tego ekranu. */
  machineIds: z.array(z.string()),
  /**
   * Automaty dostępne PRZEZ GRUPĘ. Dostęp w SPS jest sumą obu dróg, więc odznaczenie automatu
   * z tej listy niczego nie odbiera — i formularz musi to powiedzieć, zanim ktoś uzna,
   * że portal nie działa.
   */
  machineIdsFromGroups: z.array(z.string()),
});

export type EmployeeDetail = z.infer<typeof EmployeeDetailSchema>;

export const fetchEmployee = (id: string) => apiGet(`/employees/${id}`, EmployeeDetailSchema);

/** Wspólny kształt zapisu — te same pola przy tworzeniu i edycji. */
export type EmployeeWritePayload = Partial<Record<PoleZnacznika, boolean>> & {
  name?: string;
  alias?: string | null;
  costCenter?: string | null;
  active?: boolean;
  rationEffect?: 1 | 2 | 3;
  dateRangeEnable?: boolean;
  dateRangeStart?: string | null;
  dateRangeEnd?: string | null;
  /**
   * ⚠️ PIN JEDZIE TYLKO Z `zmienPin: true`. Pole jest puste zawsze (PIN-u nie da się odczytać),
   * więc bez tego przełącznika każda edycja nazwiska kasowałaby ludziom kod do automatu.
   */
  zmienPin?: boolean;
  pin?: string;
  /** ⚠️ `undefined` = nie ruszaj powiązań, `[]` = odbierz wszystkie. To NIE jest to samo. */
  employeeGroupIds?: string[];
  machineIds?: string[];
};

export const createEmployee = (payload: EmployeeWritePayload & { groupId: string; spsUserNumber: string; name: string }) =>
  apiSend(
    'POST',
    '/employees',
    payload,
    z.object({ opId: z.string(), id: z.string(), spsUserNumber: z.string() }),
  );

/**
 * IMPORT HURTEM — JEDNO żądanie na cały wsad.
 *
 * ⚠️⚠️ NIE PĘTLA PO `createEmployee`. Pętla dałaby N operacji w rejestrze zmian i N transakcji na
 * bazie klienta, a przy porażce w połowie — kadrę bez części ludzi, o której nikt nie wie, że jest
 * niepełna. Trasa `POST /employees/import` składa CAŁY wsad w jedną operację config-down: agent
 * stosuje ją w jednej transakcji na bazie SPS, więc albo wchodzi cała kadra, albo nikogo.
 *
 * ⚠️ `machineIds` I `employeeGroupIds` SIEDZĄ W KAŻDYM WPISIE, nie obok listy. Kształt jest
 * pochodną schematu zakładania jednego pracownika (`omit({ groupId })`), więc mapowanie kolumn
 * Excela może kiedyś nadawać powiązania per osoba bez ruszania API.
 *
 * ⚠️⚠️ POLE POMINIĘTE ZNACZY „NIE RUSZAJ" i to jest sedno aktualizacji: klient sam decyduje,
 * które klucze wstawić. Wysłanie pełnego obiektu z pustymi wartościami przepisałoby cały wiersz
 * pracownika przy pliku, który ma dwie kolumny.
 *
 * ⚠️ DWA PRZEŁĄCZNIKI SĄ OBOK LISTY, nie w każdym wpisie, bo dotyczą CAŁEGO importu:
 * `nadpisujIstniejace` to zgoda na tknięcie kadry, `grupyZastapuj` mówi, czy grupa z pliku
 * zastępuje dotychczasowe członkostwa, czy się do nich dokłada.
 */
export const importujPracownikow = (
  groupId: string,
  pracownicy: (EmployeeWritePayload & { spsUserNumber: string })[],
  opcje: { nadpisujIstniejace: boolean; grupyZastapuj: boolean },
) =>
  apiSend(
    'POST',
    '/employees/import',
    { groupId, ...opcje, pracownicy },
    z.object({ opId: z.string(), ile: z.number(), nowych: z.number(), aktualizacji: z.number() }),
  );

export const updateEmployee = (id: string, payload: EmployeeWritePayload) =>
  apiSend('PATCH', `/employees/${id}`, payload, z.object({ opId: z.string() }));

/**
 * PODPOWIEDZI CENTRUM KOSZTOWEGO — kody realnie używane u pracowników, plus słownik.
 *
 * ⚠️ NIE POCHODZĄ ZE SŁOWNIKA `UserCostCenters`, bo ten jest u klientów PUSTY: na DBW zero encji
 * przy 472 osobach z kodem wpisanym z ręki, na warsztacie tak samo. Lista rozwijana budowana ze
 * słownika była więc pusta i nowego centrum nie dało się ani wybrać, ani wpisać.
 *
 * `wUzyciu` jest częścią odpowiedzi, nie ozdobą: pokazuje, ile osób ma dany kod, więc od razu
 * widać, który wariant jest tym właściwym, a który literówką (na warsztacie
 * „PRT- Utrzymanie ruchu" u 9 osób i „PRT-Utrzymanie ruchu" u jednej — dla SPS to DWA różne
 * centra, bo spacja w środku jest znacząca).
 */
export const CentrumPodpowiedzSchema = z.object({
  code: z.string(),
  wUzyciu: z.number(),
  wSlowniku: z.boolean(),
});

export type CentrumPodpowiedz = z.infer<typeof CentrumPodpowiedzSchema>;

export const fetchCostCenterSuggestions = (groupId: string) =>
  apiGet(`/employees/cost-centers?groupId=${groupId}`, z.object({ podpowiedzi: z.array(CentrumPodpowiedzSchema) }));

/**
 * ZMIANA NUMERU KARTY — osobna operacja, nie pole w formularzu edycji.
 *
 * ⚠️ To przeniesienie klucza naturalnego po SIEDMIU tabelach SPS — liście przepisanej ze
 * zdekompilowanej procedury producenta (`kaskada-klucza.ts`). Dlatego własna trasa i własne okno,
 * a nie pole obok nazwiska: literówka przy edycji nie może uruchamiać kaskady na bazie klienta.
 *
 * ⚠️ `zmienHistorie` DOMYŚLNIE `false`. Przepisanie rejestru wydań robi coś, czego SPS nie robi.
 */
export const zmienNumerKarty = (
  id: string,
  payload: { groupId: string; nowyNumer: string; zmienHistorie: boolean },
) =>
  apiSend(
    'POST',
    `/employees/${id}/card-number`,
    payload,
    z.object({ opId: z.string(), stary: z.string(), nowy: z.string(), wydan: z.number() }),
  );

/**
 * HISTORIA NUMERU KARTY — jakie numery ten pracownik miał wcześniej.
 *
 * ⚠️ TYLKO ODCZYT i bez uprawnień administratora, tak jak historia limitu: pytanie „pod jakim
 * numerem szukać jego starszych wydań" zadaje obsługa. Źródłem jest kolejka config-down, więc
 * są tu WYŁĄCZNIE zmiany zrobione przez portal — patrz `api/src/employees/historia-karty.ts`.
 */
export const WpisHistoriiKartySchema = z.object({
  id: z.string(),
  kiedy: z.string(),
  zNumeru: z.string(),
  naNumer: z.string(),
  /** Konto portalu, program po API albo `null`. */
  kto: z.string().nullable(),
  tytul: z.string(),
  stan: z.string(),
  /** `null` = zmiana jeszcze NIE dojechała do maszyn; automat zna wtedy stary numer. */
  potwierdzono: z.string().nullable(),
});

export type WpisHistoriiKarty = z.infer<typeof WpisHistoriiKartySchema>;

export const fetchHistoriaKarty = (id: string, groupId: string) =>
  apiGet(
    `/employees/${id}/historia-karty?groupId=${encodeURIComponent(groupId)}`,
    z.object({ numer: z.string(), wpisy: z.array(WpisHistoriiKartySchema) }),
  );

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  KTÓRE Z TYCH NUMERÓW KART JUŻ SĄ NA WYSPIE — pod podgląd importu.
 *
 *  ⚠️⚠️ ZASTĄPIŁO PYTANIE O PIERWSZE 500 PRACOWNIKÓW. Podgląd porównywał wsad ze stroną listy
 *  (`pageSize: 500`, sortowanie po numerze karty rosnąco), więc przy kadrze większej niż 500 osoby
 *  o wysokich numerach wypadały poza nią — wiersz wyglądał na NOWY, choć nadpisywał istniejącego
 *  człowieka, i nie było ani plakietki „nadpisanie", ani różnicy „stare → nowe". Zgłoszone
 *  18.09.2026. Serwer przy zapisie i tak czytał wszystkich, więc ekran mówił co innego niż zapis.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
const PracownicyPoNumerachSchema = z.object({
  rows: z.array(
    z.object({
      spsUserNumber: z.string(),
      name: z.string(),
      alias: z.string().nullable(),
      costCenter: z.string().nullable(),
      active: z.boolean(),
      rationEffect: z.number(),
      groups: z.array(z.object({ id: z.string(), name: z.string() })),
    }),
  ),
});

export const pracownicyPoNumerach = (groupId: string, numery: readonly string[]) =>
  apiPost('/employees/po-numerach', { groupId, numery }, PracownicyPoNumerachSchema);

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  USUWANIE KART Z AUTOMATÓW (29.09.2026) — lista kart wklejona, jedna karta w wierszu.
 *
 *  ⚠️ KSZTAŁT JAK W SERWISIE (`api/src/usuwanie-kart/usuwanie-kart.service.ts`), NIE OSTRZEJ —
 *  schemat ciaśniejszy od odpowiedzi pokazałby udany podgląd jako błąd.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** Numery z pola „jedna karta w wierszu" — puste wiersze wypadają, powtórzenia liczy serwer. */
export const parsujListeKart = (tekst: string): string[] =>
  tekst
    .split(/\r?\n/)
    .map((w) => w.trim())
    .filter((w) => w !== '');

/** Lustro `MAKS_KART_NARAZ` z `api/src/usuwanie-kart/usuwanie-kart.ts`. */
export const MAKS_KART_NARAZ = 5000;

const WierszUsunieciaSchema = z.object({
  numer: z.string(),
  nazwa: z.string().nullable(),
  stan: z.enum(['DO_USUNIECIA', 'JUZ_USUNIETA', 'NIEZNANA']),
  przypisan: z.number(),
  grup: z.number(),
  mpk: z.number(),
  operacji: z.number(),
  limitow: z.number(),
  dostepow: z.number(),
  dostawcow: z.number(),
  wydan: z.number(),
});

export type WierszUsuniecia = z.infer<typeof WierszUsunieciaSchema>;

const PodgladUsunieciaKartSchema = z.object({
  wiersze: z.array(WierszUsunieciaSchema),
  powtorzen: z.number(),
  sumy: z.object({
    doUsuniecia: z.number(),
    juzUsunietych: z.number(),
    nieznanych: z.number(),
    przypisan: z.number(),
    grup: z.number(),
    mpk: z.number(),
    operacji: z.number(),
    limitow: z.number(),
    dostepow: z.number(),
    dostawcow: z.number(),
    wydanDoOdpiecia: z.number(),
  }),
  wezly: z.array(z.string()),
  operacji: z.number(),
  wymaganaWersja: z.string(),
  starszeAgenty: z.array(z.object({ nodeId: z.string(), version: z.string().nullable() })),
});

export type PodgladUsunieciaKart = z.infer<typeof PodgladUsunieciaKartSchema>;

export const podgladUsunieciaKart = (groupId: string, numery: readonly string[]) =>
  apiSend('POST', '/employees/usuwanie/podglad', { groupId, numery }, PodgladUsunieciaKartSchema);

const WynikUsunieciaKartSchema = z.object({
  opIds: z.array(z.string()),
  operacji: z.number(),
  /** Zablokowane przed usunięciem (`ALLOWLOGIN = 0`, 03.11.2026) — bez już zablokowanych. */
  zablokowanych: z.number(),
  oznaczonych: z.number(),
  usunietychTrwale: z.number(),
  blad: z.string().nullable(),
});

export type WynikUsunieciaKart = z.infer<typeof WynikUsunieciaKartSchema>;

export const usunKarty = (payload: {
  groupId: string;
  numery: readonly string[];
  trwale: boolean;
  potwierdzamOdpiecie: boolean;
}) => apiSend('POST', '/employees/usuwanie', payload, WynikUsunieciaKartSchema);
